import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormField } from "../../components/AdminDataModal";
import type { Column } from "../../components/DataTable";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";
import DataTable from "../../components/DataTable";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";

type OfferOn = "services" | "software" | "market_price_analysis";

const offerOnOptions = [
  { label: "Services", value: "services" },
  { label: "Software", value: "software" },
  { label: "Market Price Analysis", value: "market_price_analysis" },
];

const normalizeOfferOn = (value: any): OfferOn => {
  if (value === "services") return "services";
  if (value === "software") return "software";
  return "market_price_analysis";
};

export default function AdminWebsiteOffers() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateOnly } = useFormat();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const [offerItems, setOfferItems] = useState<any[]>([]);
  const [activeOfferOn, setActiveOfferOn] = useState<OfferOn>(
    "market_price_analysis",
  );

  const formatDateForInput = (value: any) => {
    if (!value) return "";

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getGeneratedLink = (row: any) => {
    const page = row?.promotion_page || "/signup";
    const safePage = page.startsWith("/") ? page : `/${page}`;

    return `${window.location.origin}${safePage}?offer=${encodeURIComponent(
      row?.offer_code || "",
    )}`;
  };

  const buildModalRow = (row: any) => ({
    ...row,
    start_date: formatDateForInput(row?.start_date),
    end_date: formatDateForInput(row?.end_date),
    offer_on: normalizeOfferOn(row?.offer_on),
    offer_item_id: row?.offer_item_id ? String(row.offer_item_id) : "",
    promotion_page: row?.promotion_page || "/signup",
    generated_link: row?.offer_code
      ? getGeneratedLink(row)
      : "Offer code will be generated after save.",
  });

  const fetchWebsiteOffers = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/admin/website-offers`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Website Offers Error:", error);
    }
  }, [BASE]);

  const fetchOfferItems = useCallback(
    async (offerOn: OfferOn) => {
      try {
        const res = await fetch(
          `${BASE}/admin/website-offers/items/${offerOn}`,
          {
            credentials: "include",
          },
        );

        const result = await res.json();

        if (result.success) {
          setOfferItems(result.data || []);
        } else {
          setOfferItems([]);
        }
      } catch (error) {
        console.error("Fetch Website Offer Items Error:", error);
        setOfferItems([]);
      }
    },
    [BASE],
  );

  useEffect(() => {
    fetchWebsiteOffers();
    fetchOfferItems("market_price_analysis");
  }, [fetchWebsiteOffers, fetchOfferItems]);

  const handleModalFormChange = useCallback(
    (formData: any) => {
      const nextOfferOn = normalizeOfferOn(formData?.offer_on);

      if (nextOfferOn !== activeOfferOn) {
        setActiveOfferOn(nextOfferOn);
        fetchOfferItems(nextOfferOn);
      }
    },
    [activeOfferOn, fetchOfferItems],
  );

  const normalizeWebsiteOfferPayload = (formData: any) => {
    const offerOn = normalizeOfferOn(formData.offer_on || activeOfferOn);
    const offerItemId = Number(formData.offer_item_id || 0);

    return {
      title: formData.title || "",
      offer_code: formData.offer_code || "",
      description: formData.description || "",
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      offer_on: offerOn,
      offer_item_id: offerItemId,
      promotion_page: formData.promotion_page || "/signup",
      status: formData.status || "active",
    };
  };

  const handleSubmit = async (formData: any) => {
    const payload = normalizeWebsiteOfferPayload(formData);

    if (!payload.title.trim()) {
      showModal({
        type: "error",
        title: "Validation Error",
        message: "Please enter offer title.",
      });
      return;
    }

    if (!payload.offer_item_id) {
      showModal({
        type: "error",
        title: "Validation Error",
        message: "Please select offer item.",
      });
      return;
    }

    const selectedItemStillValid = offerItems.some(
      (item) => Number(item.id) === Number(payload.offer_item_id),
    );

    if (!selectedItemStillValid) {
      showModal({
        type: "error",
        title: "Validation Error",
        message: "Please select offer item again after changing Offer On.",
      });
      return;
    }

    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/website-offers`
          : `${BASE}/admin/website-offers/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        await fetchWebsiteOffers();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Website offer added successfully."
              : "Website offer updated successfully.",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Unable to save website offer.",
        });
      }
    } catch (error) {
      console.error("Submit Website Offer Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving website offer.",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Website Offer",
      message: "Are you sure you want to delete this website offer?",
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE}/admin/website-offers/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            await fetchWebsiteOffers();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Website offer deleted successfully.",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Unable to delete website offer.",
            });
          }
        } catch (error) {
          console.error("Delete Website Offer Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Something went wrong while deleting website offer.",
          });
        }
      },
    });
  };

  const websiteOfferFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      { key: "title", label: "Title", colSpan: 2 },
      {
        key: "offer_code",
        label: "Offer Code",
        colSpan: 2,
      },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        colSpan: 1,
      },
      {
        key: "start_date",
        label: "From",
        type: "date",
        colSpan: 2,
      },
      {
        key: "end_date",
        label: "To",
        type: "date",
        colSpan: 2,
      },
      {
        key: "offer_on",
        label: "Offer On",
        type: "select",
        options: offerOnOptions,
        colSpan: 2,
      },
      {
        key: "offer_item_id",
        label: "Offer Item",
        type: "select",
        options: offerItems.map((item) => ({
          label:
            activeOfferOn === "market_price_analysis" && item.amount
              ? `${item.title} - MYR ${Number(item.amount || 0).toFixed(2)}`
              : item.title,
          value: String(item.id),
        })),
        colSpan: 2,
      },
      {
        key: "promotion_page",
        label: "Promotion Page",
        colSpan: 2,
      },
      {
        key: "generated_link",
        label: "Generated Promotion Link",
        disabled: true,
        colSpan: 2,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [offerItems, activeOfferOn],
  );

  const columns: Column[] = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "title", label: "Title" },
      { key: "offer_code", label: "Code" },
      {
        key: "offer_on",
        label: "Offer On",
        render: (row: any) =>
          row.offer_on === "services"
            ? "Services"
            : row.offer_on === "software"
              ? "Software"
              : "Market Price Analysis",
      },
      {
        key: "offer_item_title",
        label: "Offer Item",
        render: (row: any) => row.offer_item_title || "-",
      },
      {
        key: "start_date",
        label: "From",
        render: (row: any) => formatDateOnly(row.start_date),
      },
      {
        key: "end_date",
        label: "To",
        render: (row: any) => formatDateOnly(row.end_date),
      },
      {
        key: "promotion_page",
        label: "Page",
        render: (row: any) => row.promotion_page || "/signup",
      },
      {
        key: "generated_link",
        label: "Promotion Link",
        render: (row: any) => (
          <button
            type="button"
            className="btn-view"
            onClick={(event) => {
              event.stopPropagation();
              window.open(
                getGeneratedLink(row),
                "_blank",
                "noopener,noreferrer",
              );
            }}
            disabled={!row.offer_code}
          >
            Generate Link
          </button>
        ),
      },
      {
        key: "status",
        label: "Status",
        render: (row: any) => (
          <span
            className={
              row.status === "active" ? "badge-success" : "badge-danger"
            }
          >
            {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
          </span>
        ),
      },
    ],
    [formatDateOnly],
  );

  return (
    <div>
      <AdminListToolkit
        title="Website Offers"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={async () => {
          const newRow = {
            id: "",
            title: "",
            offer_code: "",
            description: "",
            start_date: "",
            end_date: "",
            offer_on: "market_price_analysis",
            offer_item_id: "",
            promotion_page: "/signup",
            generated_link: "Offer code will be generated after save.",
            status: "active",
          };

          setModalMode("add");
          setActiveOfferOn("market_price_analysis");
          await fetchOfferItems("market_price_analysis");
          setActiveRow(newRow);
          setModalOpen(true);
        }}
        onView={async (row) => {
          const offerOn = normalizeOfferOn(row.offer_on);

          setModalMode("view");
          setActiveOfferOn(offerOn);
          await fetchOfferItems(offerOn);
          setActiveRow(buildModalRow(row));
          setModalOpen(true);
        }}
        onEdit={async (row) => {
          const offerOn = normalizeOfferOn(row.offer_on);

          setModalMode("edit");
          setActiveOfferOn(offerOn);
          await fetchOfferItems(offerOn);
          setActiveRow(buildModalRow(row));
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Website Offer"
        fields={websiteOfferFields}
        rowData={activeRow}
        onClose={() => {
          setModalOpen(false);
          setActiveRow(null);
        }}
        onSubmit={handleSubmit}
        onFormChange={handleModalFormChange}
        size="xl"
      />
    </div>
  );
}
